
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useProfileData } from "@/hooks/useProfileData";

interface TaskDialogProps {
  tenderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  task?: any;
}

export const TaskDialog = ({ tenderId, open, onOpenChange, onSuccess, task }: TaskDialogProps) => {
  const { toast } = useToast();
  const { t } = useTranslation('offerManagement');
  const { profile } = useProfileData();
  const { register, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      title: "",
      description: "",
      status: "todo",
      priority: "medium",
      due_date: "",
      assignee_id: "",
    },
  });

  // Effect to set form values when editing a task
  useEffect(() => {
    if (task) {
      setValue("title", task.title);
      setValue("description", task.description || "");
      setValue("status", task.status);
      setValue("priority", task.priority);
      setValue("due_date", task.due_date ? new Date(task.due_date).toISOString().slice(0, 16) : "");
      setValue("assignee_id", task.assignee_id || "");
    } else {
      reset({
        title: "",
        description: "",
        status: "todo",
        priority: "medium",
        due_date: "",
        assignee_id: "",
      });
    }
  }, [task, setValue, reset]);

  // Fetch team members for the tender, including the current user
  const { data: teamMembers = [], isLoading: isMembersLoading } = useQuery({
    queryKey: ['team-members', tenderId, open],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("No user found");

        // First, get the current user's profile
        const { data: currentUserProfile, error: currentUserError } = await supabase
          .from('profiles')
          .select('id, company_name, email')
          .eq('id', user.id)
          .single();

        if (currentUserError) console.error("Error fetching current user:", currentUserError);

        // Then get team members
        const { data: members, error } = await supabase
          .from('tender_team_members')
          .select(`
            user_id,
            profiles:user_id (
              id,
              company_name,
              email
            )
          `)
          .eq('tender_id', tenderId);

        if (error) throw error;

        // Get the tender owner
        const { data: tender } = await supabase
          .from('saved_tenders')
          .select(`
            user_id,
            owner:user_id (
              id,
              company_name,
              email
            )
          `)
          .eq('id', tenderId)
          .single();

        // Create a map to track unique user IDs
        const uniqueMembers = new Map();
        
        // Add current user if authenticated and profile exists
        if (currentUserProfile) {
          uniqueMembers.set(currentUserProfile.id, {
            id: currentUserProfile.id,
            company_name: currentUserProfile.company_name,
            email: currentUserProfile.email
          });
        }
        
        // Add team members if they have profiles
        if (members && members.length > 0) {
          members.forEach(member => {
            if (member.profiles && !uniqueMembers.has(member.user_id)) {
              uniqueMembers.set(member.user_id, {
                id: member.user_id,
                company_name: member.profiles.company_name,
                email: member.profiles.email
              });
            }
          });
        }
        
        // Add owner if not already in the list
        if (tender && tender.owner && !uniqueMembers.has(tender.user_id)) {
          uniqueMembers.set(tender.user_id, {
            id: tender.user_id,
            company_name: tender.owner.company_name,
            email: tender.owner.email
          });
        }
        
        // Convert map to array
        return Array.from(uniqueMembers.values());
      } catch (error) {
        console.error("Error fetching team members:", error);
        return [];
      }
    },
    enabled: Boolean(tenderId && open),
    refetchOnWindowFocus: false,
  });

  const onSubmit = async (data: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      // Handle empty assignee_id - set to null explicitly
      const assignee_id = data.assignee_id && data.assignee_id.trim() !== "" 
        ? data.assignee_id 
        : null;

      // Only verify assignee_id if it's not null
      if (assignee_id) {
        // First check if this assignee is in our local teamMembers list
        const assigneeExists = teamMembers.some((member: any) => member.id === assignee_id);
        
        if (!assigneeExists) {
          throw new Error("Selected assignee is not a valid team member");
        }
        
        // Double check against database to ensure the profile exists
        const { data: assigneeCheck, error: checkError } = await supabase
          .from("profiles")
          .select("id")
          .eq("id", assignee_id)
          .single();

        if (checkError || !assigneeCheck) {
          throw new Error(`Invalid assignee: ${checkError?.message || "User not found in profiles table"}`);
        }
      }

      const formattedData = {
        ...data,
        assignee_id: assignee_id,
        // Handle empty due_date properly
        due_date: data.due_date ? data.due_date : null,
      };

      if (task) {
        const { error } = await supabase
          .from("tender_tasks")
          .update({
            ...formattedData,
            updated_at: new Date().toISOString(),
          })
          .eq("id", task.id);

        if (error) throw error;

        toast({
          title: t("tasks.taskUpdated"),
          description: t("tasks.taskUpdatedDesc"),
        });
      } else {
        const { error } = await supabase
          .from("tender_tasks")
          .insert([
            {
              tender_id: tenderId,
              creator_id: user.id,
              ...formattedData,
            },
          ]);

        if (error) throw error;

        toast({
          title: t("tasks.taskCreated"),
          description: t("tasks.taskCreatedDesc"),
        });
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error saving task:", error);
      toast({
        title: t("errorMessages.generalError"),
        description: error.message || t("tasks.taskError"),
        variant: "destructive",
      });
    }
  };

  // Add current user as default option
  const currentUserOption = profile?.id ? {
    id: profile.id,
    company_name: profile.company_name || profile.email || "Me",
    email: profile.email
  } : null;

  // Check if team members are loading
  if (isMembersLoading && open) {
    console.log("Loading team members...");
  }

  // Generate assignee options, making sure the current user is always included
  const assigneeOptions = !teamMembers.length ? 
    (currentUserOption ? [currentUserOption] : []) :
    (currentUserOption && !teamMembers.some(member => member.id === currentUserOption.id) ?
      [...teamMembers, currentUserOption] : teamMembers);

  // Debug team members
  useEffect(() => {
    if (open && assigneeOptions.length) {
      console.log("Available assignees:", assigneeOptions);
    }
  }, [open, assigneeOptions]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{task ? t("tasks.editTask") : t("tasks.createTask")}</DialogTitle>
          <DialogDescription>{t("tasks.dialogDescription")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">{t("tasks.taskTitle")}</Label>
            <Input
              id="title"
              {...register("title", { required: true })}
              placeholder={t("tasks.taskTitlePlaceholder")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t("tasks.description")}</Label>
            <Textarea
              id="description"
              {...register("description")}
              placeholder={t("tasks.descriptionPlaceholder")}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">{t("tasks.priority")}</Label>
              <Select
                onValueChange={(value) => setValue("priority", value)}
                defaultValue={task?.priority || "medium"}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("tasks.filter.priority")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t("tasks.filter.low")}</SelectItem>
                  <SelectItem value="medium">{t("tasks.filter.medium")}</SelectItem>
                  <SelectItem value="high">{t("tasks.filter.high")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">{t("tasks.status")}</Label>
              <Select
                onValueChange={(value) => setValue("status", value)}
                defaultValue={task?.status || "todo"}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("tasks.filter.status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todo">{t("tasks.filter.todo")}</SelectItem>
                  <SelectItem value="in_progress">{t("tasks.filter.inProgress")}</SelectItem>
                  <SelectItem value="in_review">{t("tasks.filter.inReview")}</SelectItem>
                  <SelectItem value="completed">{t("tasks.filter.completed")}</SelectItem>
                  <SelectItem value="blocked">{t("tasks.filter.blocked")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assignee_id">{t("tasks.assignTo")}</Label>
            <Select
              onValueChange={(value) => setValue("assignee_id", value)}
              defaultValue={task?.assignee_id || ""}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("team.roleSelect")} />
              </SelectTrigger>
              <SelectContent>
                {/* Fix: Use a unique non-empty string for the unassigned option */}
                <SelectItem value="unassigned">{t("tasks.unassigned")}</SelectItem>
                {assigneeOptions.map((member: any) => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.company_name || member.email || member.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="due_date">{t("tasks.dueDate")}</Label>
            <div className="relative">
              <Input
                id="due_date"
                type="datetime-local"
                {...register("due_date")}
                className="pl-10 bg-white border-gray-200 hover:border-primary focus:border-primary focus:ring-primary text-gray-900 placeholder-gray-500"
                style={{
                  colorScheme: 'light',
                }}
              />
              <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-primary" />
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("tasks.cancel")}
            </Button>
            <Button type="submit">
              {task ? t("tasks.save") : t("tasks.createTask")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
